import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Download, Trash2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useContent, useContentStats, useDeleteContent, useDownloadContent } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { usePagination } from '@/hooks/use-pagination'
import { ContentVersion } from '@/types'
import { UploadContentModal } from '@/components/UploadContentModal'
import { ContentDetailModal } from '@/components/ContentDetailModal'
import { toDisplayDate } from '@/lib/utils'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

export default function Content() {
  const { toast } = useToast()
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [search, setSearch] = useState('')
  const [fileType, setFileType] = useState<string>('all')
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const { data: statsData } = useContentStats()
  const { data, isLoading, error } = useContent({
    search,
    page,
    limit: 25,
    file_type: fileType === 'all' ? undefined : fileType,
  })
  const deleteContentMutation = useDeleteContent()
  const downloadContentMutation = useDownloadContent()

  const {
    items: content,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    firstPage()
  }

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Are you sure you want to delete "${title}"?`)) return

    try {
      await deleteContentMutation.mutateAsync(id)
      toast({
        title: 'Success',
        description: 'File deleted successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete file'),
        variant: 'destructive',
      })
    }
  }

  const handleDownload = async (id: string, title: string) => {
    try {
      const blob = await downloadContentMutation.mutateAsync(id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = title
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast({
        title: 'Success',
        description: 'File downloaded successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to download file'),
        variant: 'destructive',
      })
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  // GRK-BRAND-6: file-type chip styling from categorical palette — matches RepositoryAssetThumbnail
  // `.tint` fill + Prussian text (WCAG AA). No chromatic Tailwind classes.
  const getFileTypeStyle = (type: string): React.CSSProperties => {
    const fam = (f: keyof typeof CATEGORICAL) => ({
      backgroundColor: CATEGORICAL[f].tint,
      color: GRACKLE_COLORS.prussianBlue,
    })
    const styleMap: Record<string, React.CSSProperties> = {
      PDF: fam('raspberry'),
      PNG: fam('navy'),
      JPG: fam('navy'),
      JPEG: fam('navy'),
      GIF: fam('navy'),
      WORD: fam('steel'),
      WORD_X: fam('steel'),
      EXCEL: fam('evergreen'),
      EXCEL_X: fam('evergreen'),
      CSV: fam('evergreen'),
      POWER_POINT: fam('clay'),
      POWER_POINT_X: fam('clay'),
      SNOTE: fam('plum'),
      HTML: fam('teal'),
      TEXT: fam('slate'),
      ZIP: fam('slate'),
    }
    return styleMap[type] || fam('slate')
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="content" />
            <h1 className="text-3xl font-bold">Content & Attachments</h1>
          </div>
        </div>
        <Button onClick={() => setUploadModalOpen(true)} aria-label="Upload file">
          <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          Upload File
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Total Files</div>
          <div className="text-2xl font-bold">{statsData?.total_files?.toLocaleString() ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">This Week</div>
          <div className="text-2xl font-bold">{statsData?.this_week?.toLocaleString() ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">This Month</div>
          <div className="text-2xl font-bold">{statsData?.this_month?.toLocaleString() ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Total Size</div>
          <div className="text-2xl font-bold">
            {statsData?.total_size ? formatFileSize(statsData.total_size) : '0 Bytes'}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <SearchWithRecent
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by title or description..."
            ariaLabel="Search files"
            entityKey="content"
            className="flex-1"
          />
          <div className="w-full md:w-48">
            <label htmlFor="file-type-filter" className="sr-only">
              Filter by file type
            </label>
            <Select value={fileType} onValueChange={setFileType}>
              <SelectTrigger id="file-type-filter" aria-label="Filter by file type">
                <SelectValue placeholder="All File Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All File Types</SelectItem>
                <SelectItem value="PDF">PDF</SelectItem>
                <SelectItem value="PNG">PNG</SelectItem>
                <SelectItem value="JPG">JPG/JPEG</SelectItem>
                <SelectItem value="WORD_X">Word</SelectItem>
                <SelectItem value="EXCEL_X">Excel</SelectItem>
                <SelectItem value="SNOTE">Salesforce Note</SelectItem>
                <SelectItem value="HTML">HTML</SelectItem>
                <SelectItem value="ZIP">ZIP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Content Table */}
      <Card>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground" role="status" aria-live="polite">
              Loading files...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-destructive" role="alert" aria-live="assertive">
              Error loading files: {explainError(error, 'Unknown error')}
            </div>
          ) : content.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground" role="status" aria-live="polite">
              No files found
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Title
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Type
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Size
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Owner
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Account
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Created
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-medium">
                    Links
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {content.map((file: ContentVersion) => (
                  <tr key={file.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText
                          className="h-4 w-4 text-muted-foreground flex-shrink-0"
                          aria-hidden="true"
                        />
                        <span className="font-medium">{file.title || 'Untitled'}</span>
                      </div>
                      {file.description && (
                        <div className="text-sm text-muted-foreground mt-1">{file.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge style={getFileTypeStyle(file.file_type)}>{file.file_type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">{formatFileSize(file.content_size)}</td>
                    <td className="px-4 py-3 text-sm">{file.owner_name || 'Unknown'}</td>
                    <td className="px-4 py-3 text-sm">
                      {file.related_accounts && file.related_accounts.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {file.related_accounts.map((acc) => (
                            <Link
                              key={acc.id}
                              to={`/accounts/${acc.id}`}
                              className="text-primary hover:underline"
                            >
                              {acc.name}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground" aria-label="No related account">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(
                        toDisplayDate(file.created_date) ?? new Date(file.created_date)
                      ).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">{file.link_count || 0}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedContentId(file.id)
                            setDetailModalOpen(true)
                          }}
                          aria-label={`View details for ${file.title}`}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(file.id, file.title)}
                          disabled={downloadContentMutation.isPending}
                          aria-label={`Download ${file.title}`}
                        >
                          <Download className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(file.id, file.title)}
                          disabled={deleteContentMutation.isPending}
                          aria-label={`Delete ${file.title}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
              {total} files
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={!hasPrevPage}
                aria-label="Go to previous page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={!hasNextPage}
                aria-label="Go to next page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <UploadContentModal open={uploadModalOpen} onOpenChange={setUploadModalOpen} />

      <ContentDetailModal
        contentId={selectedContentId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
    </div>
  )
}
